import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Radio } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { NewsCard, type NewsItem } from "@/components/NewsSection";
import HVIHeader from "@/components/HVIHeader";
import HVIFooter from "@/components/HVIFooter";
import { buildSeo, SITE_URL, PUBLISHER } from "@/lib/seo";

export const Route = createFileRoute("/news")({
  component: NewsArchivePage,
  head: () =>
    buildSeo({
      path: "/news",
      title: "Hantavirus Outbreak News Archive — Hantavirus International",
      description:
        "Full archive of hantavirus outbreak news: WHO, CDC, UKHSA and global media coverage of the MV Hondius cluster, Andes virus cases and country reports.",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          url: `${SITE_URL}/news`,
          name: "Hantavirus Outbreak News Archive",
          publisher: PUBLISHER,
        },
      ],
    }),
});

function NewsArchivePage() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("news")
        .select("id,title,description,url,source,published_at,importance_score,is_critical")
        .order("published_at", { ascending: false })
        .limit(500);
      if (!cancelled) {
        setItems((data ?? []) as NewsItem[]);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <HVIHeader />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground hover:text-hvi-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to live map
        </Link>

        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-hvi-primary mb-2">
              <Radio className="w-3 h-3 animate-pulse" />
              <span>News Archive</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              All hantavirus outbreak coverage
            </h1>
            <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-2xl">
              Every news story we have logged for the current hantavirus outbreak — sortable
              chronologically with original publication dates and source links.
            </p>
          </div>
          <div className="text-[10px] font-data uppercase tracking-wider text-muted-foreground border border-border px-2.5 py-1 rounded-full">
            {items.length} stories
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-xl">
            Loading news…
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-xl">
            No news yet.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((n) => (
              <NewsCard key={n.id} item={n} variant="standard" />
            ))}
          </div>
        )}
      </main>
      <HVIFooter />
    </div>
  );
}
